'use client'

import { useReferencesProductFilters } from '@/feature/product/hooks/useReferencesProductFilters'
import { referencesProductFiltersEnrichedAtom } from '@/feature/product/services/referencesProduct.store'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { FilterGroup } from '@/ui/form/inputs/filter-group/FilterGroup.client'
import type { TabDefinition } from '@/ui/tabs/Tabs.client'
import { Tabs } from '@/ui/tabs/Tabs.client'
import { useAtomValue } from 'jotai'
import { useCallback, useMemo, useState } from 'react'

export function ReferencesProductFiltersDesktop() {
  const { t } = useTranslation(['referencesProductFilters'])

  const enrichedFilters = useAtomValue(referencesProductFiltersEnrichedAtom)

  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(
    () => enrichedFilters[0]?.labelWithUnit,
  )

  const { handleFilterChange, handleResetFilters, totalActivesFilters } =
    useReferencesProductFilters()

  const handleTabChange = useCallback((tabId: string | undefined) => {
    if (tabId) {
      pushToGtmDataLayer({
        event: 'gaEvent',
        eventCategory: 'fiche produit multi-discriminant',
        eventAction: 'tableau de références - clic discriminant',
        eventLabel: tabId,
      })
    }

    setSelectedTabId(tabId)
  }, [])

  const tabs: TabDefinition[] = useMemo(() => {
    return enrichedFilters.map((filter, index) => ({
      label: filter.labelWithUnit,
      id: filter.labelWithUnit,
      badge: filter.valuesChecked.length > 0 ? filter.valuesChecked.length : undefined,
      content: (
        <FilterGroup
          key={filter.label}
          multiple
          hideTitle
          filter={{
            label: String(index),
            values: filter.values.map((value) => ({
              label: value.label,
              value: value.label,
            })),
          }}
          selectedValues={filter.valuesChecked}
          onSelectionChange={(label, selectedFilter) => {
            handleFilterChange(label, selectedFilter)
            pushToGtmDataLayer({
              event: 'gaEvent',
              eventCategory: 'fiche produit multi-discriminant',
              eventAction: 'tableau de références - clic valeur discriminant',
              eventLabel: `${filter.label} | ${selectedFilter}`,
            })
          }}
          disabledValues={filter.values
            .filter((value) => value.disabled)
            .map((value) => value.label)}
        />
      ),
    }))
  }, [enrichedFilters, handleFilterChange])

  const onResetFilters = useCallback(() => {
    handleResetFilters()
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'fiche produit multi-discriminant',
      eventAction: 'tableau de références',
      eventLabel: 'effacer tous les filtres',
    })
  }, [handleResetFilters])

  return (
    <Tabs
      variant="md"
      className="hidden desktop:flex"
      tabs={tabs}
      aria-label={t('referencesProductFilters:tabsLabel')}
      actions={
        totalActivesFilters > 0
          ? [
              <Button size="md" key="reset-filters" variant="link" onPress={onResetFilters}>
                {t('referencesProductFilters:reset')}
              </Button>,
            ]
          : []
      }
      selectedTabId={selectedTabId}
      onTabChange={handleTabChange}
      scrollable
    />
  )
}
